/* eslint-disable no-console */

import React, { useMemo, useEffect, useState } from 'react';

import { useHistory } from 'react-router-dom'
import { useRouterContext } from 'hooks';
import { useUserContext, isUserSubwebAdmin } from 'context/UserContext';

import styled from 'styled-components/macro';
import * as Yup from 'yup';
import { Formik, Field } from 'formik';
import PropTypes from 'prop-types';

import {
  FieldCustom, FieldImageUpload, FieldSelect, ModalConfirm
} from 'components/molecules';
import {
  Form, Button, Text, Spacer,
} from 'components/atoms';

import { useQueryImage, updateImage, deleteImage } from 'services/requests';
import { generateEditorPath } from 'services/generators'
import { toast } from 'react-toastify';
import { Toast } from 'containers';

export const ImageSchema = Yup.object().shape({
  id: Yup.string(),
  title: Yup.string(),
  description: Yup.string(),
  language: Yup.string()
    .oneOf(['pt', 'en'], 'Lingua inválida')
    .required('Campo obrigatório'),
  alt: Yup.string().required('Campo obrigatório'),
  collection: Yup.string()
});

// todo language parameter
const MediaUpdateForm = ({
  imageId,
  // eslint-disable-next-line no-unused-vars
  onSubmit,
  // eslint-disable-next-line no-unused-vars
  isActive,
  language,
  isPage,
  onDiscard
}) => {
  const [initialValues, setInitialValues] = useState({
    id: imageId,
    title: '',
    description: '',
    language,
    alt: '',
    collection: '',
    permissionLevel: 2,
    isLocal: false
  })
  const { match: { params: { subweb } } } = useRouterContext()

  const [{ user }] = useUserContext()
  const isAdmin = (user && user.admin) || isUserSubwebAdmin(user, subweb);
  const [permissionLevelOptions] = useState([
    { label: 'apenas para mim', value: 1 },
    { label: 'para esta subweb', value: 2 },
    isAdmin ? { label: 'para todas as subwebs', value: 3 } : null
  ])
  const [confirmRemove, setConfirmRemove] = useState(false)
  const history = useHistory();
  const { loading: imageLoading, response, error: imageError } = useQueryImage({
    id: imageId,
    subweb
  });

  useEffect(() => {
    if (
      response
      && response.data
      && response.data.files
      && !imageLoading
      && !imageError
    ) {
      const {
        data: { files: portalImage },
      } = response;
      const {
        url, id, data: imageDataArr, permissionLevel, date
      } = portalImage;

      const urlObj = new URL(url);
      urlObj.searchParams.set('date', date);
      const updateUrl = urlObj.toString();

      const imageDataLang = imageDataArr.find(
        ({ languageName }) => languageName === language
      );

      let initialValues = {
        url: updateUrl,
        id,
        alt: '',
        title: '',
        description: '',
        language,
        permissionLevel
      };

      if (imageDataLang) {
        const { alt, title, description } = imageDataLang;
        initialValues = {
          ...initialValues,
          alt,
          title,
          description
        };
      }
      setInitialValues(initialValues);
    }
  }, [response, imageLoading, imageError, language]);

  const isInitialValid = useMemo(() => ImageSchema.isValidSync(initialValues), [
    initialValues,
  ]);

  const [loading, setLoading] = useState(false);
  const handleSubmit = async values => {
    const {
      alt: altValue,
      title: titleValue,
      language: languageValue,
      file: fileValue
    } = values;

    try {
      setLoading(true);
      const {
        id, alt, description, title, image: file, permissionLevel
      } = values;

      const languageImageData = {
        alt: altValue,
        description: altValue,
        title: titleValue,
        language: languageValue
      };
      await updateImage({
        id,
        language: [languageImageData],
        description,
        alt,
        title,
        subweb,
        file,
        permissionLevel
      });
      setLoading(false);
      toast.success(
        <Toast
          type="success"
          title="Edição concluída"
          description="O ficheiro foi editado com sucesso."
        />
      );

    } catch (err) {
      setLoading(false);
      toast.error(
        <Toast
          type="error"
          title="Erro a editar ficheiro"
          description={err.message}
        />
      );
      console.error(err);
    }
  };

  const handleRemove = async (remove) => {
    if (remove === true) {
      try {
        setLoading(true);
        await deleteImage({ fileId: imageId, subweb })

        setLoading(false);
        toast.success(
          <Toast
            type="success"
            title="Remoção concluída"
            description="A imagem foi apagada com sucesso."
          />
        );

        history.push(`/${subweb}/assets`)

        // TODO: redirect to gallery
      } catch (err) {
        setLoading(false);
        toast.error(
          <Toast
            type="error"
            title="Erro a remover imagen"
            description={err.message}
          />
        );
        console.error(err);
      }
    }
    else {
      setConfirmRemove(false)
    }
  }

  const onGalleryDiscard = () => {
    history.goBack();
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        isInitialValid={isInitialValid}
        validationSchema={ImageSchema}
        onSubmit={(values, { setSubmitting }) => {
          handleSubmit(values);
          setSubmitting(false);
        }}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <Form>
            <Scrollable isPage={isPage}>
              <Spacer bottom="m">
                <Spacer bottom="s">
                  <Text modifiers={['base']}>Imagem</Text>
                </Spacer>
                <Field
                  name="image"
                  component={FieldImageUpload}
                  isEditingImage
                  imageUrl={initialValues}
                />
              </Spacer>
              <Field
                name="title"
                placeholder="Ex: Campus"
                title="Título"
                component={FieldCustom}
              />
              <Field
                name="alt"
                placeholder="Ex: Academic Ranking of World Universities"
                title="Descrição da imagem"
                component={FieldCustom}
              />
              <Field
                title="Disponibilizar imagem:"
                name="permissionLevel"
                component={FieldSelect}
                placeholder="Selectione o nível de permissão..."
                options={permissionLevelOptions}
              />
            </Scrollable>
            <Actions>
              {/* <Button style={{ marginRight: 'auto' }} type="button" modifiers={['marginRight', 'capitalize', 'danger']} onClick={() => setConfirmRemove(true)}>
                Remover Imagem
              </Button> */}
              <Button type="button" modifiers={['marginRight', 'capitalize']} onClick={onDiscard || onGalleryDiscard}>
                Voltar
              </Button>
              <Button
                type="submit"
                modifiers={['primary', 'capitalize']}
                loading={isSubmitting || loading}
              >
                Editar
              </Button>
            </Actions>

          </Form>
        )}
      </Formik>
      <ModalConfirm
        isOpen={confirmRemove}
        toggle={() => setConfirmRemove(!confirmRemove)}
        onChange={(e) => { handleRemove(e) }}
        title="Apagar imagem"
        message="A imagem será removida de todas as páginas onde esteja a ser usada, pretende continuar?"
        textConfirm="Continuar"
        textDiscard="Cancelar"
      />
    </>
  );
};
const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${props => props.theme.spacing.l};
  padding-right: ${props => props.theme.spacing.xl2};
`;

const Scrollable = styled.div`
  height: ${props => (props.isPage ? 'auto' : '404px')};
  ${props => !props.isPage && 'overflow: auto'};
  padding-right: ${props => props.theme.spacing.xl2};

  /* width */
  ::-webkit-scrollbar {
    width: 5px;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.bg};
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.colors.bgDarker};
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background-color: ${props => props.theme.colors.bgDarker};
  }
`;


MediaUpdateForm.propTypes = {
  imageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSubmit: PropTypes.func,
  onDiscard: PropTypes.func,
  isActive: PropTypes.bool,
  language: PropTypes.string,
  isPage: PropTypes.bool,
};

MediaUpdateForm.defaultProps = {
  imageId: null,
  onSubmit: () => null,
  onDiscard: null,
  isActive: false,
  language: 'pt',
  isPage: true,
};

export default MediaUpdateForm;
